import { useEffect, useRef } from 'react'

import { baseApi, browserWentOffline } from '@krp/client-runtime'

import { LIVE_HANDLERS } from '../composition/live'
import { checkServer, connectionCheckRequested, scheduleNextCheck, selectConnection } from './connection'
import { useAppDispatch, useAppSelector } from './hooks'

/**
 * Монитор связи с сервером — без разметки, монтируется один раз в корне приложения.
 *
 * - события браузера `offline`/`online` (и `navigator.onLine` при запуске) переводят связь в «нет сети» / «проверяем»;
 * - пока связи нет — проверки доступности с паузами `HEALTH_TIMINGS.serverCheckPausesMs`; срок следующей лежит в срезе
 *   (`nextCheckAt`), чтобы окно показало обратный отсчёт. Проверки идут и без сети на устройстве (К-111): браузер присылает
 *   `online` не всегда, а ответ сервера возвращает связь сам;
 * - связь вернулась — перечитать то, что могло измениться за обрыв: те же типы тегов, что после переподключения живого
 *   канала (`LIVE_HANDLERS.resync`). Весь кэш не сбрасываем (`resetApiState` стёр бы данные на экране), а запросы,
 *   упавшие ошибкой связи, тегов не дают — их перечитывают сами экраны (`useRefetchOnReconnect`) или их опрос.
 */
export function ConnectionMonitor() {
  const dispatch = useAppDispatch()
  const { status, nextCheckAt } = useAppSelector(selectConnection)
  const inFlight = useRef(false)

  useEffect(() => {
    const offline = () => dispatch(browserWentOffline({ at: Date.now() }))
    // Сеть на устройстве появилась — проверить сервер сразу, а не через паузу.
    const online = () => dispatch(connectionCheckRequested())
    if (navigator.onLine === false) offline()
    // Окно вернулось из свёрнутого: таймеры там замирали, а сеть могла уже вернуться — проверить сервер сразу.
    const visible = () => {
      if (document.visibilityState === 'visible' && navigator.onLine !== false) dispatch(connectionCheckRequested())
    }
    window.addEventListener('offline', offline)
    window.addEventListener('online', online)
    document.addEventListener('visibilitychange', visible)
    return () => {
      window.removeEventListener('offline', offline)
      window.removeEventListener('online', online)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [dispatch])

  useEffect(() => {
    if (status === 'online') return undefined
    if (nextCheckAt === null) {
      dispatch(scheduleNextCheck())
      return undefined
    }
    const timer = window.setTimeout(() => {
      // Проверка уже идёт («Повторить сейчас» во время проверки): её итог сам назначит следующую.
      if (inFlight.current) return
      inFlight.current = true
      void dispatch(checkServer()).finally(() => {
        inFlight.current = false
      })
    }, Math.max(0, nextCheckAt - Date.now()))
    return () => window.clearTimeout(timer)
  }, [dispatch, status, nextCheckAt])

  const wasOnline = useRef(status === 'online')
  useEffect(() => {
    const was = wasOnline.current
    wasOnline.current = status === 'online'
    if (status !== 'online' || was) return
    const types = LIVE_HANDLERS.resync()
    if (types.length > 0) dispatch(baseApi.util.invalidateTags(types))
  }, [dispatch, status])

  return null
}
